// Angular
import { Component, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

// Cer
import { CerGridComponent } from 'src/cer/cer-grid/cer-grid.component';
import { FieldMetadata, FieldWidthEnum, ViewMetadata } from 'src/cer/cer-data/cer-data-types';
import { UiActionTypeEnum, UiCommand, UiCommandEvent, UiKeyboardShortcut } from '@ui/cer-ui-types';
import { CerFormPanesSettingsUtil } from '@form/cer-form-panes/cer-form-panes.service';

// Royalty
import { RoyaltyService } from './royalty.service';
import { RoyaltyApproveStatusFormatter } from './royalty-approve-status-formatter';
import { ICerFormViewCustomComponent } from '@form/cer-form-view-type/icer-form-view-custom-component';
import { CerFormView } from '@form/cer-form-view';
import { CerFormComponent } from '@form/cer-form.component';
import { CerFormPaneComponent } from '@form/cer-form-panes/cer-form-pane/cer-form-pane.component';

@Component({
  selector: 'royalty-approve',
  templateUrl: './royalty-approve.component.html',
  providers: [RoyaltyService]
})

export class RoyaltyApproveComponent implements ICerFormViewCustomComponent {

  protected formView: CerFormView;
  public setView(view: CerFormView): void {
    this.formView = view;
  }

  public onViewCommand(e: UiCommandEvent): void {
    this.form?.formService.formCommandNext(e);
  }
  
  private form: CerFormComponent;
  @ViewChild(CerFormComponent) set formContent(content: CerFormComponent) {
    if (content && !this.form) {
      this.form = content;
    }
  }
  
  // Panes
  protected panes = CerFormPanesSettingsUtil.main();
  @ViewChildren(CerFormPaneComponent) set setPaneComponents(paneComponents: QueryList<CerFormPaneComponent>) {
    if (paneComponents?.length > 0 && this.form != null) {
      this.form.initContext(paneComponents);
    }
  }

  protected viewMetadata: ViewMetadata = {
    name: 'royalty-approve', dataApiName: "RoyaltyAfregningAktuelView", text: "Royalty godkendelser", textSingular: "Royalty godkendelser",
    primaryKey: 'id', allowEdit: false, allowCreate: false, allowDelete: false
  };

  protected fieldMetadata: FieldMetadata[] = [
    { name: "id", text: "Royalty ID", visible: false, allowEdit: false },
    { name: "redaktorNavn", text: "Redaktør" },
    { name: "afregningId", text: "Modtager ID", width: "60px" },
    { name: "afregningNavn", text: "Modtager", width: "90px", foreignKeyUsing: 'afregingId' },
    { name: "forlagId", text: "Forlag ID", width: "60px", foreignKey: { navigations: [{ link: 'fk', foreignKey: { parentEntity: 'Forfatter', parentColumn: 'num' } }] } },
    { name: "forlagNavn", text: "Forlag", width: "90px", foreignKeyUsing: 'forlagId' },
    { name: "forfatterNavn", text: "Forfatter", foreignKeyUsing: 'forlagId' },
    { name: "hID", text: "Varenummer ID", visible: false },
    { name: "hovedKontraktId", text: "HKontrakt ID", visible: false },
    { name: "hovedKontrakt", text: "Hovedkontrakt", visible: false },
    { name: "kontrakt", text: "Kontrakt", width: "80px", foreignKey: { navigations: [{ link: 'fk', foreignKey: { entity: 'ContractProjectBasicView', parentColumn: 'num' } }] } },
    { name: "isbn", text: "ISBN", visible: false, foreignKey: { navigations: [{ link: 'fk', foreignKey: { entity: 'ContractProjectBasicView', parentColumn: 'isbn' } }] } },
    { name: "vareNR", text: "Varenr", visible: false },
    { name: "arbTitel", text: "Arb. titel" },
    { name: "bogTitel", text: "Bog titel", visible: false },
    { name: "kontraktType", text: "Kontrakt type", visible: false },
    { name: "afregningstype", text: "Afregn. type", visible: false },
    { name: "afregningAdresse1", text: "Modtager adresse 1", visible: false },
    { name: "afregningAdresse2", text: "Modtager adresse 2", visible: false },
    { name: "afregningAdresse3", text: "Modtager adresse 3", visible: false },
    { name: "afregningLand", text: "Modtager Land", visible: false },
    { name: "forlagAdresse1", text: "Adresse 1", visible: false },
    { name: "forlagAdresse2", text: "Adresse 2", visible: false },
    { name: "forlagAdresse3", text: "Adresse 3", visible: false },
    { name: "forlagLand", text: "Land", visible: false },
    { name: "endeligSalgspris", text: "Endelig salgspris", format: 'Amount', visible: false },
    { name: "royaltyAmount", text: "Royalty beløb", format: 'Amount' },
    { name: "valutaKode", text: "Valuta", width: "40px", foreignKeyType: 'currency' },
    { name: "valutaKurs", text: "Kurs", format: 'Amount', visible: false },
    { name: "godkendt", text: "Godkendt", visible: false },
    { name: "godkendtAf", text: "Godkendt af", visible: false, foreignKeyType: 'userShortName' },
    { name: "godkendtDato", text: "Godkendt dato", visible: false, format: 'DateShort' },
    { name: "afsluttet", text: "Afsluttet", visible: false, },
    { name: "afsluttetAf", text: "Afsluttet af", visible: false, foreignKeyType: 'userShortName' },
    { name: "afsluttetDato", text: "Afsluttet dato", visible: false, format: 'DateShort' },
    { name: "createdDate", text: "Beregnet dato", visible: false, format: 'DateShort' },
    { name: "status", text: "Status kode", visible: false },
    { name: "statusText", text: "Status", widthType: FieldWidthEnum.XXL, formatter: new RoyaltyApproveStatusFormatter() }
  ];

  private showDetailsCmd: UiCommand = { id: 'details', text: "Vis detaljer (Alt+O)", iconCss: "e-menu-icon e-icons e-edit", target: ".e-content" };

  protected contextMenuCommands: UiCommand[] =
    [
      this.showDetailsCmd,
      { id: 'sep', text: '', separator: 'content' },
      { id: 'wordMergeDraft', text: "Dan afregning (kladde)", iconCss: "e-menu-icon e-icons e-wordexport", target: ".e-content" },
      { id: 'pdfMergeDraft', text: "Dan afregning (kladde/PDF) ", iconCss: "e-menu-icon e-icons e-pdfexport", target: ".e-content" },
    ];

  protected toolbarCommands: UiCommand[] =
    [
      this.showDetailsCmd
    ];

  protected keyboardShortcuts: UiKeyboardShortcut[] =
    [
      { cmd: this.showDetailsCmd, code: 'KeyO', alt: true, ctrl: false, shift: false, enabled: true }
    ]

  // Get grid control to handle grouping collapse/showDropArea
  private grid: CerGridComponent;
  @ViewChild(CerGridComponent) set setGrid(content: CerGridComponent) {
    if (content && !this.grid) {
      this.grid = content;
    }
  }

  constructor(
    private route: ActivatedRoute,
    private royaltyService: RoyaltyService,
    public dialog: MatDialog) {

    this.route.data.subscribe(data => this.initFormRouteData(data));
  }

  protected onCommand(e: UiCommandEvent) {

    switch (e.actionType) {
      case UiActionTypeEnum.Created:
        this.gridInit();
        break;
    }
    this.royaltyService.onGridCommand(e, this.dialog, this.grid);
  }

  // Route paramters
  private isViewSet: boolean = false;
  private isViewEditor: boolean = false;
  private isViewPublisher: boolean = false;

  private initFormRouteData(data: any) {
    if (data && data.hasOwnProperty('view')) {
      switch (data['view']) {
        case 'editor':
          this.isViewEditor = true;
          break;
        case 'publisher':
          this.isViewPublisher = true;
          break;
      }
      this.isViewSet = true;
    }
  }

  private gridInit() {
    if (this.grid?.setupService && this.isViewSet) {
      if (this.isViewPublisher) {
        this.grid.setupService.doColumnGroup('forlagNavn');
      }
      if (this.isViewEditor) {
        this.grid.setupService.doColumnGroup('redaktorNavn');
      }
    }
  }

}
